import React, { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, InputMui, Tabs } from '../../../../../../components';
import { useDebounce } from '../../../../../../hooks/debounce/use-debounce';
import { ChannelBox } from '../../../../components/channel/box';
import { SCENE_TYPE, useGroupChannels } from '../../hooks/use-group-channels';
import { CheckChannel, DeviceChannelsCheck, GroupedDeviceChannelsCheckbox } from '../../types';
import { DeviceChannelsGrouped } from './grouped-channel-box';
import './index.scss';

type GroupedChannelsProps = {
  items: GroupedDeviceChannelsCheckbox[];
  setItems: Dispatch<SetStateAction<GroupedDeviceChannelsCheckbox[]>>;
  visibility?: boolean;
};

const GroupedChannels: React.FC<GroupedChannelsProps> = ({ items, setItems, visibility }) => {
  const { t: tc } = useTranslation('common');
  const [activeTabs, setActiveTabs] = useState<number>(0);
  const { inputValue, inputQuery, setInputValue } = useDebounce();
  const { tabs, handleChannelOnChange } = useGroupChannels({
    items,
    setItems,
    visibility: visibility,
    withScenes: true,
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => setInputValue(event.target.value);

  const filteredChannels = (
    items
      .filter((x) => x.type !== SCENE_TYPE)
      .map((groupedDeviceChannels: GroupedDeviceChannelsCheckbox) => ({
        ...groupedDeviceChannels,
        devices: groupedDeviceChannels.devices
          .filter(
            (dev: DeviceChannelsCheck) =>
              dev.channels.filter((ch) => ch.channel.Name.toLowerCase().includes(inputQuery.toLowerCase())).length > 0,
          )
          .map((dev) => ({
            ...dev,
            channels: dev.channels.filter((ch) => ch.channel.Name.toLowerCase().includes(inputQuery.toLowerCase())),
          })),
      })) as GroupedDeviceChannelsCheckbox[]
  ).filter((groupedDeviceChannels: GroupedDeviceChannelsCheckbox) => groupedDeviceChannels.devices.length > 0);

  const filteredActions = (items.find((x) => x.type === SCENE_TYPE)?.devices || [])
    .flatMap((x) => x.channels)
    .sort((a, b) => a.channel.Name.localeCompare(b.channel.Name));

  return (
    <div className="exalus-grouped-device-channels">
      {!visibility && (
        <Tabs
          tabList={tabs}
          activeTabIndex={activeTabs}
          setActiveTabIndex={setActiveTabs}
          tabsType="default"
          autoScrollDisabled
        />
      )}
      {activeTabs === 0 ? (
        <div className="m-t-24">
          <InputMui
            value={inputValue}
            onChange={handleChange}
            placeholder={tc('search')}
            sx={{ marginBottom: 0 }}
            clearEnabled
          />
          <div className="m-t-16">
            {filteredChannels.map((groupedDeviceChannels: GroupedDeviceChannelsCheckbox) => (
              <Accordion
                kind="custom"
                key={groupedDeviceChannels.label}
                title={groupedDeviceChannels.label}
                selectedItems={groupedDeviceChannels.devices.flatMap((x) => x.channels).filter((y) => y.checked).length}
                totalItems={groupedDeviceChannels.devices.flatMap((x) => x.channels).length}
                expanded={!!inputQuery}
              >
                <div className="grid-list-16">
                  {groupedDeviceChannels.devices.map((deviceChannels) => (
                    <DeviceChannelsGrouped
                      key={deviceChannels.device.Guid}
                      deviceChannels={deviceChannels}
                      groupLabel={groupedDeviceChannels.type}
                      handleChannelOnChange={handleChannelOnChange}
                      visibility={visibility}
                    />
                  ))}
                </div>
              </Accordion>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid-list-16 m-t-16">
          {filteredActions.map((checkChannel: CheckChannel) => (
            <ChannelBox
              key={checkChannel.id}
              channel={checkChannel.channel}
              isListItem
              isCheckbox
              onChannelToggle={() => handleChannelOnChange(checkChannel.id, SCENE_TYPE)}
              checked={checkChannel.checked}
              disableRedirect
              showIsHidden={checkChannel.channel.IsHidden && !visibility}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default GroupedChannels;
